import * as React from 'react';
import Button from '@mui/material/Button';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Popover,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AndroidIcon from '@mui/icons-material/Android';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import {Chip} from '@mui/material';
import {Alert, Snackbar} from '@mui/material';
import {analyzeHtmlTemplate, createHtmlTemplate, deleteHtmlTemplate, generateHtmlTemplateLocalizations, getHtmlTemplate, getImprovementRequest, improveHtmlTemplate, updateHtmlTemplate, validateHtmlTemplate} from '../Api';
import {useNavigate, useParams} from 'react-router-dom';
import HtmlTemplateDto, {HtmlTemplateLocalization, HtmlTemplateUpsertDto} from '../models/dtos/HtmlTemplateDto';
import HtmlTemplatePreview from '../components/HtmlTemplatePreview';

const SUPPORTED_LANGUAGES = ['en', 'de', 'fr', 'it', 'ro', 'ru', 'es', 'pt', 'pl', 'tr'];
const PREVIEW_PRICE_TIERS = [0.99, 1.99, 2.99, 3.99, 4.99, 9.99, 14.99, 19.99, 29.99, 59.99];
const PREVIEW_CONFIG_STORAGE_KEY = 'html-template-preview-config';

interface HtmlTemplatePreviewConfig {
    language: string;
    oldPrice: number;
    newPrice: number;
    discountPercent: number;
    betcoins: number;
    expirationHours: number;
}

const DEFAULT_PREVIEW_CONFIG: HtmlTemplatePreviewConfig = {
    language: 'en',
    oldPrice: 19.99,
    newPrice: 9.99,
    discountPercent: 50,
    betcoins: 100000,
    expirationHours: 24
};

const clampNumber = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const sanitizePreviewConfig = (config: Partial<HtmlTemplatePreviewConfig> | null | undefined): HtmlTemplatePreviewConfig => {
    const language = config?.language && SUPPORTED_LANGUAGES.includes(config.language) ? config.language : DEFAULT_PREVIEW_CONFIG.language;
    const oldPrice = config?.oldPrice && PREVIEW_PRICE_TIERS.includes(config.oldPrice) ? config.oldPrice : DEFAULT_PREVIEW_CONFIG.oldPrice;
    const newPrice = config?.newPrice && PREVIEW_PRICE_TIERS.includes(config.newPrice) ? config.newPrice : DEFAULT_PREVIEW_CONFIG.newPrice;
    const discountPercent = clampNumber(
        typeof config?.discountPercent === 'number' ? Math.round(config.discountPercent) : DEFAULT_PREVIEW_CONFIG.discountPercent,
        1,
        99
    );
    const betcoins = clampNumber(
        typeof config?.betcoins === 'number' ? Math.round(config.betcoins) : DEFAULT_PREVIEW_CONFIG.betcoins,
        1000,
        1_000_000_000
    );
    const expirationHours = clampNumber(
        typeof config?.expirationHours === 'number' ? Math.round(config.expirationHours) : DEFAULT_PREVIEW_CONFIG.expirationHours,
        1,
        24 * 365
    );

    return {
        language,
        oldPrice,
        newPrice,
        discountPercent,
        betcoins,
        expirationHours
    };
};

const loadPreviewConfig = (): HtmlTemplatePreviewConfig => {
    if (typeof window === 'undefined') return DEFAULT_PREVIEW_CONFIG;

    try {
        const raw = window.localStorage.getItem(PREVIEW_CONFIG_STORAGE_KEY);
        if (!raw) return DEFAULT_PREVIEW_CONFIG;
        return sanitizePreviewConfig(JSON.parse(raw));
    } catch {
        return DEFAULT_PREVIEW_CONFIG;
    }
};

const normalizeLocalizedKeys = (localizedKeys: string[]): string[] => (
    Array.from(new Set(localizedKeys.map(key => key.trim()).filter(key => key.length > 0)))
);

const normalizeHtmlTemplateLocalizations = (
    localizedKeys: string[],
    localizations: HtmlTemplateLocalization[]
): HtmlTemplateLocalization[] => {
    const localizationMap = new Map(
        localizations
            .filter(localization => localization.id != null)
            .map(localization => [localization.id as string, localization])
    );
    return normalizeLocalizedKeys(localizedKeys).map(key => {
        const existing = localizationMap.get(key);
        return {
            id: key,
            localizations: {
                ...(existing?.localizations ?? {}),
                en: existing?.localizations?.en ?? ''
            }
        };
    });
};

const mergeHtmlTemplateLocalizations = (
    localizedKeys: string[],
    primaryLocalizations: HtmlTemplateLocalization[],
    fallbackLocalizations: HtmlTemplateLocalization[]
): HtmlTemplateLocalization[] => {
    const primaryMap = new Map(
        primaryLocalizations
            .filter(localization => localization.id != null)
            .map(localization => [localization.id as string, localization])
    );
    const fallbackMap = new Map(
        fallbackLocalizations
            .filter(localization => localization.id != null)
            .map(localization => [localization.id as string, localization])
    );
    return normalizeLocalizedKeys(localizedKeys).map(key => {
        const primary = primaryMap.get(key);
        const fallback = fallbackMap.get(key);
        return {
            id: key,
            localizations: {
                ...(fallback?.localizations ?? {}),
                ...(primary?.localizations ?? {}),
                en: primary?.localizations?.en ?? fallback?.localizations?.en ?? ''
            }
        };
    });
};

export default function AddHtmlTemplatePage() {
    let editedTemplateId = useParams()['id'] as string;
    let isEditMode = editedTemplateId != undefined;

    const [state, setState] = React.useState({
        templatePresent: false,
        name: '',
        htmlContent: '',
        localizations: [] as HtmlTemplateLocalization[],
        localizedKeys: [] as string[],
        betcoinsMacrosKey: null as string | null,
        oldPriceKeys: [] as string[],
        newPriceKeys: [] as string[],
        expirationTimestampMacrosKey: null as string | null,
        discountMacroKey: null as string | null,
        dialogOpen: false,
        dialogMsg: '',
        deleteDialogOpen: false,
        addKeyDialogOpen: false,
        newKeyName: '',
        osType: 'android' as string,
        isValid: true as boolean,
        errorMsg: null as string | null,
        analyzing: false,
        generatingLocalizations: false,
        validating: false,
        snackbarOpen: false,
        snackbarMsg: '',
        snackbarSeverity: 'success' as 'success' | 'error'
    });
    const [oldPriceInput, setOldPriceInput] = React.useState('');
    const [newPriceInput, setNewPriceInput] = React.useState('');
    const [localizedKeyInput, setLocalizedKeyInput] = React.useState('');
    const [aiPrompt, setAiPrompt] = React.useState('');
    const [aiImproving, setAiImproving] = React.useState(false);
    const [expandedLocalizationKey, setExpandedLocalizationKey] = React.useState<string | false>(false);
    const [previewConfig, setPreviewConfig] = React.useState<HtmlTemplatePreviewConfig>(() => loadPreviewConfig());
    const [previewConfigAnchorEl, setPreviewConfigAnchorEl] = React.useState<HTMLElement | null>(null);
    const templateFileInputRef = React.useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    const localizationRows = React.useMemo(
        () => normalizeHtmlTemplateLocalizations(state.localizedKeys, state.localizations),
        [state.localizedKeys, state.localizations]
    );

    React.useEffect(() => {
        const fetchData = async () => {
            if (isEditMode) {
                let template = await getHtmlTemplate(editedTemplateId);
                if (template != null) {
                    const normalizedLocalizedKeys = normalizeLocalizedKeys(template.localizedKeys || []);
                    setState(prevState => ({
                        ...prevState,
                        name: template.name,
                        htmlContent: template.htmlContent,
                        localizations: normalizeHtmlTemplateLocalizations(normalizedLocalizedKeys, template.localizations || []),
                        localizedKeys: normalizedLocalizedKeys,
                        betcoinsMacrosKey: template.betcoinsMacrosKey || null,
                        oldPriceKeys: template.oldPriceKeys || [],
                        newPriceKeys: template.newPriceKeys || [],
                        expirationTimestampMacrosKey: template.expirationTimestampMacrosKey || null,
                        discountMacroKey: template.discountMacroKey || null,
                        osType: template.osType || 'android',
                        isValid: template.isValid,
                        errorMsg: template.errorMsg || null,
                        templatePresent: true
                    }));
                }
            }
        };
        fetchData();
    }, []);

    React.useEffect(() => {
        if (typeof window === 'undefined') return;
        window.localStorage.setItem(PREVIEW_CONFIG_STORAGE_KEY, JSON.stringify(previewConfig));
    }, [previewConfig]);

    const addLocalizationKey = (keyName: string) => {
        const normalizedKey = keyName.trim();
        if (!normalizedKey) return;

        if (state.localizedKeys.includes(normalizedKey)) {
            setState(prev => ({
                ...prev,
                dialogMsg: `Key "${normalizedKey}" already exists`,
                dialogOpen: true
            }));
            return;
        }

        setState(prev => ({
            ...prev,
            localizedKeys: normalizeLocalizedKeys([...prev.localizedKeys, normalizedKey]),
            localizations: normalizeHtmlTemplateLocalizations(
                [...prev.localizedKeys, normalizedKey],
                [...prev.localizations, {id: normalizedKey, localizations: {en: ''}}]
            )
        }));
        setLocalizedKeyInput('');
        setExpandedLocalizationKey(normalizedKey);
    };

    const removeLocalizationKey = (keyName: string) => {
        setState(prev => ({
            ...prev,
            localizedKeys: prev.localizedKeys.filter(key => key !== keyName),
            localizations: prev.localizations.filter(localization => localization.id !== keyName)
        }));
        setExpandedLocalizationKey(prev => prev === keyName ? false : prev);
    };

    const updateTranslation = (keyName: string, lang: string, value: string) => {
        setState(prev => {
            const existingLocalization = prev.localizations.find(localization => localization.id === keyName);
            const nextLocalizations = prev.localizations.filter(localization => localization.id !== keyName);
            nextLocalizations.push({
                id: keyName,
                localizations: {
                    ...(existingLocalization?.localizations ?? {}),
                    [lang]: value
                }
            });
            return {
                ...prev,
                localizations: normalizeHtmlTemplateLocalizations(prev.localizedKeys, nextLocalizations)
            };
        });
    };

    const exportTemplate = () => {
        const template: HtmlTemplateDto = {
            id: editedTemplateId ?? '',
            name: state.name,
            htmlContent: state.htmlContent,
            localizations: state.localizations,
            localizedKeys: state.localizedKeys,
            betcoinsMacrosKey: state.betcoinsMacrosKey,
            oldPriceKeys: state.oldPriceKeys,
            newPriceKeys: state.newPriceKeys,
            expirationTimestampMacrosKey: state.expirationTimestampMacrosKey,
            discountMacroKey: state.discountMacroKey,
            osType: state.osType,
            createdAt: '',
            isValid: state.isValid,
            errorMsg: state.errorMsg
        };

        const blob = new Blob([JSON.stringify(template, null, 2)], {type: 'application/json'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${state.name || 'template'}_exported.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const importTemplate = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const template = JSON.parse(ev.target?.result as string) as Partial<HtmlTemplateDto>;
                if (typeof template.name !== 'string' || typeof template.htmlContent !== 'string' || !Array.isArray(template.localizations)) {
                    throw new Error('Invalid template file');
                }

                const importedName = template.name;
                const importedHtmlContent = template.htmlContent;
                const localizedKeys = normalizeLocalizedKeys(
                    Array.isArray(template.localizedKeys)
                        ? template.localizedKeys
                        : template.localizations.map(localization => localization.id ?? '')
                );

                setState(prev => ({
                    ...prev,
                    name: importedName,
                    htmlContent: importedHtmlContent,
                    localizations: normalizeHtmlTemplateLocalizations(localizedKeys, template.localizations ?? []),
                    localizedKeys,
                    betcoinsMacrosKey: template.betcoinsMacrosKey ?? null,
                    oldPriceKeys: Array.isArray(template.oldPriceKeys) ? template.oldPriceKeys : [],
                    newPriceKeys: Array.isArray(template.newPriceKeys) ? template.newPriceKeys : [],
                    expirationTimestampMacrosKey: template.expirationTimestampMacrosKey ?? null,
                    discountMacroKey: template.discountMacroKey ?? null,
                    osType: template.osType || 'android',
                    isValid: typeof template.isValid === 'boolean' ? template.isValid : prev.isValid,
                    errorMsg: template.errorMsg ?? null
                }));
            } catch {
                setState(prev => ({...prev, dialogMsg: 'Invalid template export file', dialogOpen: true}));
            }
        };
        reader.readAsText(file);
        if (templateFileInputRef.current) templateFileInputRef.current.value = '';
    };

    const onSaveClicked = async () => {
        let hasError = false;
        let errorMsg = '';

        if (!state.name || state.name.trim().length < 2) {
            hasError = true;
            errorMsg = "Template name must be at least 2 characters";
        } else if (!state.htmlContent || state.htmlContent.trim().length === 0) {
            hasError = true;
            errorMsg = "HTML content is required";
        }

        if (!hasError) {
            const dto: HtmlTemplateUpsertDto = {
                name: state.name.trim(),
                htmlContent: state.htmlContent,
                localizations: state.localizations,
                betcoinsMacrosKey: state.betcoinsMacrosKey,
                expirationTimestampMacrosKey: state.expirationTimestampMacrosKey,
                discountMacroKey: state.discountMacroKey,
                oldPriceKeys: state.oldPriceKeys,
                newPriceKeys: state.newPriceKeys,
                localizedKeys: state.localizedKeys,
                osType: state.osType
            };

            let result;
            if (isEditMode) {
                result = await updateHtmlTemplate(editedTemplateId, dto);
            } else {
                result = await createHtmlTemplate(dto);
            }

            if (!result) {
                hasError = true;
                errorMsg = "Server error!";
            }
        }

        if (hasError) {
            setState(prev => ({...prev, dialogMsg: errorMsg, dialogOpen: true}));
        } else {
            navigate('/funnels/html-templates');
        }
    };

    const onAnalyzeClicked = async () => {
        if (!state.htmlContent || state.htmlContent.trim().length === 0) {
            setState(prev => ({...prev, dialogMsg: 'HTML content is required for analysis', dialogOpen: true}));
            return;
        }
        setState(prev => ({...prev, analyzing: true}));
        const analysis = await analyzeHtmlTemplate(state.htmlContent);
        if (!analysis) {
            setState(prev => ({...prev, analyzing: false, dialogMsg: 'Analysis failed', dialogOpen: true}));
            return;
        }
        setState(prev => {
            const newLocalizedKeys = normalizeLocalizedKeys(analysis.localizedKeys);
            return {
                ...prev,
                analyzing: false,
                localizedKeys: newLocalizedKeys,
                localizations: mergeHtmlTemplateLocalizations(newLocalizedKeys, analysis.localizations, prev.localizations),
                betcoinsMacrosKey: analysis.betcoinsMacrosKey,
                expirationTimestampMacrosKey: analysis.expirationTimestampMacrosKey,
                discountMacroKey: analysis.discountMacroKey,
                oldPriceKeys: analysis.oldPriceKeys,
                newPriceKeys: analysis.newPriceKeys,
                snackbarOpen: true,
                snackbarMsg: 'Localizations and keys were extracted from the template',
                snackbarSeverity: 'success'
            };
        });
    };

    const onImproveWithAiClicked = async () => {
        if (!aiPrompt.trim()) return;
        if (!isEditMode) return;
        setAiImproving(true);
        const response = await improveHtmlTemplate(editedTemplateId, aiPrompt.trim());
        if (!response) {
            setAiImproving(false);
            setState(prev => ({...prev, dialogMsg: 'AI improvement failed. Please try again.', dialogOpen: true}));
            return;
        }

        let result = response;
        while (result.status === 'PENDING') {
            await new Promise(resolve => setTimeout(resolve, 5000));
            const polled = await getImprovementRequest(result.id);
            if (!polled) {
                setAiImproving(false);
                setState(prev => ({...prev, dialogMsg: 'AI improvement failed. Please try again.', dialogOpen: true}));
                return;
            }
            result = polled;
        }

        setAiImproving(false);

        if (result.status === 'FAILED' || !result.htmlContent || !result.analysis) {
            setState(prev => ({...prev, dialogMsg: 'AI improvement failed. Please try again.', dialogOpen: true}));
            return;
        }

        const analysis = result.analysis;
        setState(prev => {
            const newLocalizedKeys = normalizeLocalizedKeys(analysis.localizedKeys);
            return {
                ...prev,
                htmlContent: result.htmlContent!,
                localizedKeys: newLocalizedKeys,
                localizations: mergeHtmlTemplateLocalizations(newLocalizedKeys, analysis.localizations, prev.localizations),
                betcoinsMacrosKey: analysis.betcoinsMacrosKey,
                expirationTimestampMacrosKey: analysis.expirationTimestampMacrosKey,
                discountMacroKey: analysis.discountMacroKey,
                oldPriceKeys: analysis.oldPriceKeys,
                newPriceKeys: analysis.newPriceKeys,
                snackbarOpen: true,
                snackbarMsg: 'AI improvement has succeeded',
                snackbarSeverity: 'success'
            };
        });
        setAiPrompt('');
    };

    const onGenerateLocalizationsClicked = async () => {
        const localizedKeys = normalizeLocalizedKeys(state.localizedKeys);
        if (localizedKeys.length === 0) {
            setState(prev => ({...prev, dialogMsg: 'Add at least one localization key first', dialogOpen: true}));
            return;
        }
        const englishTranslations = Object.fromEntries(
            localizationRows
                .filter(localization => localization.id != null)
                .map(localization => [localization.id as string, localization.localizations.en ?? ''])
        );
        setState(prev => ({...prev, generatingLocalizations: true}));
        const response = await generateHtmlTemplateLocalizations({
            localizedKeys,
            englishTranslations,
            supportedLanguages: SUPPORTED_LANGUAGES
        });
        if (!response) {
            setState(prev => ({
                ...prev,
                generatingLocalizations: false,
                dialogMsg: 'Failed to generate localizations',
                dialogOpen: true
            }));
            return;
        }
        const generatedLocalizations = response.localizations || [];
        setState(prev => ({
            ...prev,
            generatingLocalizations: false,
            localizations: normalizeHtmlTemplateLocalizations(
                localizedKeys,
                mergeHtmlTemplateLocalizations(localizedKeys, generatedLocalizations, prev.localizations).map(localization => ({
                    ...localization,
                    localizations: {
                        ...localization.localizations,
                        en: prev.localizations.find(existing => existing.id === localization.id)?.localizations.en
                            ?? localization.localizations.en
                            ?? ''
                    }
                }))
            )
        }));
    };

    const onResetLocalizationsClicked = () => {
        setState(prev => ({
            ...prev,
            localizations: normalizeHtmlTemplateLocalizations(
                prev.localizedKeys,
                prev.localizedKeys.map(key => ({
                    id: key,
                    localizations: Object.fromEntries(
                        SUPPORTED_LANGUAGES.map(lang => [lang, ''])
                    )
                }))
            )
        }));
    };

    const onValidateClicked = async () => {
        setState(prev => ({...prev, validating: true}));
        const dto: HtmlTemplateUpsertDto = {
            name: state.name.trim(),
            htmlContent: state.htmlContent,
            localizations: state.localizations,
            betcoinsMacrosKey: state.betcoinsMacrosKey,
            expirationTimestampMacrosKey: state.expirationTimestampMacrosKey,
            discountMacroKey: state.discountMacroKey,
            oldPriceKeys: state.oldPriceKeys,
            newPriceKeys: state.newPriceKeys,
            localizedKeys: state.localizedKeys,
            osType: state.osType
        };
        const result = await validateHtmlTemplate(dto);
        if (!result) {
            setState(prev => ({
                ...prev,
                validating: false,
                snackbarOpen: true,
                snackbarMsg: 'Validation failed',
                snackbarSeverity: 'error'
            }));
            return;
        }
        setState(prev => ({
            ...prev,
            validating: false,
            isValid: result.isValid,
            errorMsg: result.errorMsg || null,
            snackbarOpen: true,
            snackbarMsg: result.isValid ? 'Validation succeded' : 'Validation failed',
            snackbarSeverity: result.isValid ? 'success' : 'error'
        }));
    };

    const onApplyClicked = async () => {
        let hasError = false;
        let errorMsg = '';

        if (!state.name || state.name.trim().length < 2) {
            hasError = true;
            errorMsg = "Template name must be at least 2 characters";
        } else if (!state.htmlContent || state.htmlContent.trim().length === 0) {
            hasError = true;
            errorMsg = "HTML content is required";
        }

        if (!hasError) {
            const dto: HtmlTemplateUpsertDto = {
                name: state.name.trim(),
                htmlContent: state.htmlContent,
                localizations: state.localizations,
                betcoinsMacrosKey: state.betcoinsMacrosKey,
                expirationTimestampMacrosKey: state.expirationTimestampMacrosKey,
                discountMacroKey: state.discountMacroKey,
                oldPriceKeys: state.oldPriceKeys,
                newPriceKeys: state.newPriceKeys,
                localizedKeys: state.localizedKeys,
                osType: state.osType
            };
            const result = await updateHtmlTemplate(editedTemplateId, dto);
            if (!result) {
                hasError = true;
                errorMsg = "Server error!";
            } else {
                const refetched = await getHtmlTemplate(editedTemplateId);
                if (refetched) {
                    setState(prev => ({
                        ...prev,
                        name: refetched.name,
                        htmlContent: refetched.htmlContent,
                        localizations: normalizeHtmlTemplateLocalizations(refetched.localizedKeys || [], refetched.localizations || []),
                        localizedKeys: normalizeLocalizedKeys(refetched.localizedKeys || []),
                        betcoinsMacrosKey: refetched.betcoinsMacrosKey || null,
                        oldPriceKeys: refetched.oldPriceKeys || [],
                        newPriceKeys: refetched.newPriceKeys || [],
                        expirationTimestampMacrosKey: refetched.expirationTimestampMacrosKey || null,
                        discountMacroKey: refetched.discountMacroKey || null,
                        osType: refetched.osType || 'android',
                        isValid: refetched.isValid,
                        errorMsg: refetched.errorMsg || null,
                        snackbarOpen: true,
                        snackbarMsg: 'Changes were applied',
                        snackbarSeverity: 'success'
                    }));
                }
            }
        }

        if (hasError) {
            setState(prev => ({...prev, dialogMsg: errorMsg, dialogOpen: true}));
        }
    };

    const onDeleteClicked = async () => {
        let result = await deleteHtmlTemplate(editedTemplateId);
        if (!result) {
            setState(prev => ({...prev, dialogMsg: "Server error!", dialogOpen: true, deleteDialogOpen: false}));
        } else {
            navigate('/funnels/html-templates');
        }
    };

    const handleDialogClose = () => {
        setState(prev => ({...prev, dialogOpen: false}));
    };

    const isPreviewConfigOpen = Boolean(previewConfigAnchorEl);
    const previewExpirationTimestamp = React.useMemo(
        () => Math.floor(Date.now() / 1000) + previewConfig.expirationHours * 60 * 60,
        [previewConfig.expirationHours]
    );

    const updatePreviewConfig = (patch: Partial<HtmlTemplatePreviewConfig>) => {
        setPreviewConfig(prev => sanitizePreviewConfig({...prev, ...patch}));
    };

    return (
        <div>
            <Stack gap="50px 50px" sx={{mx: 5, my: 5}} justifyContent='center' alignItems='center'>
                <Grid container spacing={2} columns={{xs: 1, sm: 2, md: 6, lg: 8}} alignItems='center'>
                    <Grid item xs={0} sm={0} md={1} lg={1}/>
                    <Grid item xs={2} sm={2} md={4} lg={6}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                            <Typography variant="h3">
                                {isEditMode ? "Edit HTML template" : "Add new HTML template"}
                            </Typography>
                            <Button variant="outlined" size="small" onClick={() => navigate('/funnels/html-templates')}>
                                Back to templates
                            </Button>
                            <input
                                ref={templateFileInputRef}
                                type="file"
                                accept=".json"
                                style={{display: 'none'}}
                                onChange={importTemplate}
                            />
                            <Button variant="outlined" size="small" onClick={exportTemplate}>
                                Export
                            </Button>
                            <Button variant="outlined" size="small" onClick={() => templateFileInputRef.current?.click()}>
                                Import
                            </Button>
                        </Box>
                    </Grid>

                    {!state.templatePresent && isEditMode ? (
                        <React.Fragment>
                            <Box width="100%"/>
                            <Grid item xs={0} sm={0} md={1} lg={1}/>
                            <Grid item xs={2} sm={2} md={4} lg={6} key="progress">
                                <CircularProgress size={64} style={{marginBottom: '32px'}}/>
                            </Grid>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            {/* Template Name + OS Type */}
                            <Box width="100%"/>
                            <Grid item xs={0} sm={0} md={1} lg={1}/>
                            <Grid item xs={2} sm={2} md={4} lg={6}>
                                <Box sx={{display: 'flex', gap: 2, alignItems: 'flex-start'}}>
                                    <TextField
                                        label="Template name"
                                        variant="filled"
                                        value={state.name}
                                        fullWidth={true}
                                        onChange={e => setState(prev => ({...prev, name: e.target.value}))}
                                    />
                                    <FormControl variant="filled" sx={{minWidth: 150, flexShrink: 0}}>
                                        <InputLabel id="os-type-inline-label">OS Type</InputLabel>
                                        <Select
                                            labelId="os-type-inline-label"
                                            value={state.osType || 'android'}
                                            onChange={e => setState(prev => ({...prev, osType: e.target.value}))}
                                            renderValue={(val) => (
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                                                    {val === 'ios'
                                                        ? <PhoneIphoneIcon sx={{fontSize: 18, color: 'text.secondary'}}/>
                                                        : <AndroidIcon sx={{fontSize: 18, color: '#3ddc84'}}/>
                                                    }
                                                    {val === 'ios' ? 'iOS' : 'Android'}
                                                </Box>
                                            )}
                                        >
                                            <MenuItem value="android">
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                    <AndroidIcon sx={{fontSize: 18, color: '#3ddc84'}}/> Android
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="ios">
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                    <PhoneIphoneIcon sx={{fontSize: 18, color: 'text.secondary'}}/> iOS
                                                </Box>
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Grid>

                            {/* HTML Content + Preview side by side */}
                            <Box width="100%"/>
                            <Grid item xs={0} sm={0} md={1} lg={1}/>
                            <Grid item xs={2} sm={2} md={4} lg={6}>
                                <Typography variant="h6" sx={{mb: 1}}>HTML Content:</Typography>
                                <Box sx={{display: 'flex', gap: 3, flexDirection: {xs: 'column', md: 'row'}, alignItems: 'flex-start'}}>
                                    <Box sx={{width: {xs: '100%', md: '35%'}, flexShrink: 0, minWidth: 0}}>
                                        <TextField
                                            label="HTML content"
                                            variant="filled"
                                            value={state.htmlContent}
                                            fullWidth={true}
                                            multiline
                                            minRows={24}
                                            maxRows={40}
                                            onChange={e => setState(prev => ({...prev, htmlContent: e.target.value}))}
                                            sx={{
                                                '& .MuiInputBase-input': {
                                                    fontFamily: 'monospace',
                                                    fontSize: '13px'
                                                }
                                            }}
                                        />
                                        <Box sx={{display: 'flex', gap: 1, mt: 1}}>
                        <Button
                                                variant="outlined"
                                                size="small"
                                                disabled={state.analyzing || !state.htmlContent}
                                                onClick={onAnalyzeClicked}
                                            >
                                                {state.analyzing ? 'Extracting...' : 'Extract keys'}
                                            </Button>
                                            {isEditMode && (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    color="success"
                                                    disabled={state.validating}
                                                    onClick={onValidateClicked}
                                                >
                                                    {state.validating ? 'Validating...' : 'Validate'}
                                                </Button>
                                            )}
                                        </Box>
                                        <Box sx={{mt: 0.5}}>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                <strong>Extract keys</strong> — extract all macros and keys from the HTML content.
                                            </Typography>
                                            {isEditMode && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    <strong>Validate</strong> — save and validate the template against the configuration and selected platform on the server.
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    <Box sx={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                    }}>
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                            {isEditMode && (
                                                <Chip
                                                    label={state.isValid ? 'Valid' : 'Invalid'}
                                                    size="small"
                                                    color={state.isValid ? 'success' : 'error'}
                                                    variant="filled"
                                                />
                                            )}
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={e => setPreviewConfigAnchorEl(e.currentTarget)}
                                            >
                                                Configure preview
                                            </Button>
                                        </Box>
                                        <HtmlTemplatePreview
                                            htmlContent={state.htmlContent}
                                            localizations={state.localizations}
                                            language={previewConfig.language}
                                            betcoinsMacrosKey={state.betcoinsMacrosKey}
                                            expirationTimestampMacrosKey={state.expirationTimestampMacrosKey}
                                            discountMacroKey={state.discountMacroKey}
                                            oldPriceKeys={state.oldPriceKeys}
                                            newPriceKeys={state.newPriceKeys}
                                            betcoins={previewConfig.betcoins}
                                            expirationTimestamp={previewExpirationTimestamp}
                                            discount={previewConfig.discountPercent / 100}
                                            oldPrice={previewConfig.oldPrice}
                                            newPrice={previewConfig.newPrice}
                                            width={375}
                                            height={667}
                                            borderRadius="24px"
                                            borderWidth="3px"
                                            interactive={true}
                                            showLabel={true}
                                        />
                                        {isEditMode && state.errorMsg && (
                                            <Box sx={{
                                                mt: 1.5,
                                                p: 1.5,
                                                borderRadius: 1,
                                                bgcolor: 'error.light',
                                                color: 'error.contrastText',
                                                width: '100%'
                                            }}>
                                                <Typography variant="body2">{state.errorMsg}</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Grid>

                            <Popover
                                open={isPreviewConfigOpen}
                                anchorEl={previewConfigAnchorEl}
                                onClose={() => setPreviewConfigAnchorEl(null)}
                                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                                transformOrigin={{vertical: 'top', horizontal: 'center'}}
                            >
                                <Stack spacing={2} sx={{p: 2.5, width: 320}}>
                                    <Typography variant="subtitle1">Preview configuration</Typography>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="preview-language-label">Language</InputLabel>
                                        <Select
                                            labelId="preview-language-label"
                                            label="Language"
                                            value={previewConfig.language}
                                            onChange={e => updatePreviewConfig({language: e.target.value})}
                                        >
                                            {SUPPORTED_LANGUAGES.map(lang => (
                                                <MenuItem key={lang} value={lang}>{lang.toUpperCase()}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="preview-old-price-label">Old price</InputLabel>
                                        <Select
                                            labelId="preview-old-price-label"
                                            label="Old price"
                                            value={String(previewConfig.oldPrice)}
                                            onChange={e => updatePreviewConfig({oldPrice: Number(e.target.value)})}
                                        >
                                            {PREVIEW_PRICE_TIERS.map(price => (
                                                <MenuItem key={`old-${price}`} value={String(price)}>{`$${price.toFixed(2)}`}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="preview-new-price-label">New price</InputLabel>
                                        <Select
                                            labelId="preview-new-price-label"
                                            label="New price"
                                            value={String(previewConfig.newPrice)}
                                            onChange={e => updatePreviewConfig({newPrice: Number(e.target.value)})}
                                        >
                                            {PREVIEW_PRICE_TIERS.map(price => (
                                                <MenuItem key={`new-${price}`} value={String(price)}>{`$${price.toFixed(2)}`}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        label="Discount"
                                        size="small"
                                        type="number"
                                        value={previewConfig.discountPercent}
                                        onChange={e => updatePreviewConfig({discountPercent: Number(e.target.value)})}
                                        inputProps={{min: 1, max: 99, step: 1}}
                                        helperText="Percent from 1 to 99"
                                        fullWidth
                                    />
                                    <TextField
                                        label="Betcoins amount"
                                        size="small"
                                        type="number"
                                        value={previewConfig.betcoins}
                                        onChange={e => updatePreviewConfig({betcoins: Number(e.target.value)})}
                                        inputProps={{min: 1000, max: 1000000000, step: 1000}}
                                        helperText="From 1,000 to 1,000,000,000"
                                        fullWidth
                                    />
                                    <TextField
                                        label="Hours till expiration"
                                        size="small"
                                        type="number"
                                        value={previewConfig.expirationHours}
                                        onChange={e => updatePreviewConfig({expirationHours: Number(e.target.value)})}
                                        inputProps={{min: 1, max: 8760, step: 1}}
                                        helperText="Used to calculate the expiration timestamp"
                                        fullWidth
                                    />
                                </Stack>
                            </Popover>

                            {/* AI Improvement */}
                            {isEditMode && (
                                <React.Fragment>
                                    <Box width="100%"/>
                                    <Grid item xs={0} sm={0} md={1} lg={1}/>
                                    <Grid item xs={2} sm={2} md={4} lg={6}>
                                        <Typography variant="h6">Improve with AI</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                                            Let AI know how you would like to improve this template
                                        </Typography>
                                        <TextField
                                            multiline
                                            minRows={3}
                                            maxRows={8}
                                            fullWidth
                                            variant="filled"
                                            placeholder="e.g. Make the call-to-action button more prominent, add a countdown timer..."
                                            value={aiPrompt}
                                            onChange={e => setAiPrompt(e.target.value)}
                                            disabled={aiImproving}
                                        />
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mt: 1}}>
                                            <Button
                                                variant="contained"
                                                disabled={aiImproving || !aiPrompt.trim() || !state.htmlContent}
                                                onClick={onImproveWithAiClicked}
                                            >
                                                Improve with AI
                                            </Button>
                                        </Box>
                                    </Grid>
                                </React.Fragment>
                            )}

                            {/* AI Improvement Loading Modal */}
                            <Dialog open={aiImproving} PaperProps={{sx: {textAlign: 'center', p: 3, minWidth: 360}}}>
                                <DialogContent sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3}}>
                                    <Box sx={{
                                        width: 64, height: 64, borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        animation: 'pulse 2s ease-in-out infinite',
                                        '@keyframes pulse': {
                                            '0%, 100%': {transform: 'scale(1)', opacity: 1},
                                            '50%': {transform: 'scale(1.15)', opacity: 0.85}
                                        }
                                    }}>
                                        <Typography sx={{fontSize: 32}}>&#10024;</Typography>
                                    </Box>
                                    <CircularProgress size={28}/>
                                    <Typography variant="body1" color="text.secondary" sx={{maxWidth: 320}}>
                                        AI is working to improve the HTML template. It can take up to a couple of minutes as it has to regenerate the template. Please wait.
                                    </Typography>
                                </DialogContent>
                            </Dialog>

                            {/* Localization Keys */}
                            <Box width="100%"/>
                            <Grid item xs={0} sm={0} md={1} lg={1}/>
                            <Grid item xs={2} sm={2} md={4} lg={6}>
                                <Typography variant="h6">Localization keys:</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Add keys with an English base translation, then expand each key to review or edit
                                    translations for the supported languages.
                                </Typography>
                                <Box sx={{display: 'flex', gap: 1.5, flexDirection: {xs: 'column', md: 'row'}, mt: 2}}>
                                    <TextField
                                        label="New localization key"
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                        value={localizedKeyInput}
                                        onChange={e => setLocalizedKeyInput(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addLocalizationKey(localizedKeyInput);
                                            }
                                        }}
                                        placeholder="e.g. headline_text"
                                    />
                                    <Button
                                        variant="outlined"
                                        onClick={() => addLocalizationKey(localizedKeyInput)}
                                        sx={{minWidth: {md: 120}}}
                                    >
                                        Add key
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        disabled={state.generatingLocalizations || localizationRows.length === 0}
                                        onClick={onGenerateLocalizationsClicked}
                                        sx={{minWidth: {md: 220}}}
                                    >
                                        {state.generatingLocalizations ? 'Generating...' : 'Generate localizations'}
                                    </Button>
                                    <Button
                                        variant="text"
                                        color="error"
                                        disabled={localizationRows.length === 0}
                                        onClick={onResetLocalizationsClicked}
                                        sx={{minWidth: {md: 170}}}
                                    >
                                        Reset localizations
                                    </Button>
                                </Box>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 1}}>
                                    Generates translations from the current English values for all supported languages.
                                </Typography>
                                {state.generatingLocalizations && (
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mt: 1}}>
                                        <CircularProgress size={18}/>
                                        <Typography variant="body2" color="text.secondary">
                                            Generating translations...
                                        </Typography>
                                    </Box>
                                )}
                                {localizationRows.length > 0 && (
                                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2}}>
                                        {localizationRows.map(localization => (
                                            <Chip
                                                key={localization.id}
                                                label={localization.id || 'Unnamed key'}
                                                color="primary"
                                                variant="outlined"
                                                onClick={() => setExpandedLocalizationKey(localization.id || false)}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Grid>

                            {localizationRows.map((loc) => (
                                <React.Fragment key={loc.id || 'localization-row'}>
                                    <Box width="100%"/>
                                    <Grid item xs={0} sm={0} md={1} lg={1}/>
                                    <Grid item xs={2} sm={2} md={4} lg={6}>
                                        <Accordion
                                            expanded={expandedLocalizationKey === loc.id}
                                            onChange={(_, expanded) => setExpandedLocalizationKey(expanded ? (loc.id || false) : false)}
                                        >
                                            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%'}}>
                                                    <Chip label={loc.id || 'Unnamed key'} color="primary" size="small"/>
                                                    <Typography variant="body2" color={loc.localizations.en ? 'text.primary' : 'text.secondary'} sx={{flex: 1, minWidth: 0}}>
                                                        {loc.localizations.en || 'No English translation yet'}
                                                    </Typography>
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 2, flexDirection: {xs: 'column', md: 'row'}, mb: 2}}>
                                                    <TextField
                                                        label="English"
                                                        variant="filled"
                                                        size="small"
                                                        fullWidth
                                                        value={loc.localizations.en || ''}
                                                        placeholder="Base English translation"
                                                        onChange={e => updateTranslation(loc.id || '', 'en', e.target.value)}
                                                    />
                                                    <Button
                                                        color="error"
                                                        variant="outlined"
                                                        onClick={() => removeLocalizationKey(loc.id || '')}
                                                    >
                                                        Remove key
                                                    </Button>
                                                </Box>
                                                <Divider sx={{mb: 2}}/>
                                                <Grid container spacing={2}>
                                                    {SUPPORTED_LANGUAGES.filter(lang => lang !== 'en').map(lang => (
                                                        <Grid item xs={12} md={6} key={`${loc.id}-${lang}`}>
                                                            <TextField
                                                                label={lang.toUpperCase()}
                                                                variant="filled"
                                                                size="small"
                                                                fullWidth
                                                                value={loc.localizations[lang] || ''}
                                                                placeholder={`Translation (${lang})`}
                                                                onChange={e => updateTranslation(loc.id || '', lang, e.target.value)}
                                                            />
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </AccordionDetails>
                                        </Accordion>
                                    </Grid>
                                </React.Fragment>
                            ))}

                            {/* Template Configuration */}
                            <Box width="100%"/>
                            <Grid item xs={0} sm={0} md={1} lg={1}/>
                            <Grid item xs={2} sm={2} md={4} lg={6}>
                                <Divider sx={{my: 2}}/>
                                <Typography variant="h6" sx={{mb: 2}}>Template configuration:</Typography>

                                <Box sx={{mb: 2}}>
                                    <TextField
                                        label="Betcoins Macros Key"
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                        value={state.betcoinsMacrosKey || ''}
                                        onChange={e => setState(prev => ({
                                            ...prev,
                                            betcoinsMacrosKey: e.target.value || null
                                        }))}
                                        placeholder="e.g. betcoins"
                                    />
                                </Box>

                                <Box sx={{mb: 2}}>
                                    <TextField
                                        label="Expiration Timestamp Macros Key"
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                        value={state.expirationTimestampMacrosKey || ''}
                                        onChange={e => setState(prev => ({
                                            ...prev,
                                            expirationTimestampMacrosKey: e.target.value || null
                                        }))}
                                        placeholder="e.g. expiration_timestamp"
                                    />
                                </Box>

                                <Box sx={{mb: 2}}>
                                    <TextField
                                        label="Discount Macro Key"
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                        value={state.discountMacroKey || ''}
                                        onChange={e => setState(prev => ({
                                            ...prev,
                                            discountMacroKey: e.target.value || null
                                        }))}
                                        placeholder="e.g. discount"
                                    />
                                </Box>

                                <Box sx={{mb: 2}}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{mb: 0.5}}>Old Price Keys:</Typography>
                                    <Box sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        p: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        minHeight: 42
                                    }}>
                                        {state.oldPriceKeys.map((key, idx) => (
                                            <Chip
                                                key={idx}
                                                label={key}
                                                size="small"
                                                variant="outlined"
                                                onDelete={() => setState(prev => ({
                                                    ...prev,
                                                    oldPriceKeys: prev.oldPriceKeys.filter((_, i) => i !== idx)
                                                }))}
                                            />
                                        ))}
                                        <TextField
                                            variant="standard"
                                            size="small"
                                            placeholder="Type and press Enter"
                                            value={oldPriceInput}
                                            onChange={e => setOldPriceInput(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' && oldPriceInput.trim()) {
                                                    e.preventDefault();
                                                    const val = oldPriceInput.trim();
                                                    if (!state.oldPriceKeys.includes(val)) {
                                                        setState(prev => ({...prev, oldPriceKeys: [...prev.oldPriceKeys, val]}));
                                                    }
                                                    setOldPriceInput('');
                                                }
                                            }}
                                            InputProps={{disableUnderline: true}}
                                            sx={{flex: 1, minWidth: 120}}
                                        />
                                    </Box>
                                </Box>

                                <Box sx={{mb: 2}}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{mb: 0.5}}>New Price Keys:</Typography>
                                    <Box sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        p: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        minHeight: 42
                                    }}>
                                        {state.newPriceKeys.map((key, idx) => (
                                            <Chip
                                                key={idx}
                                                label={key}
                                                size="small"
                                                variant="outlined"
                                                onDelete={() => setState(prev => ({
                                                    ...prev,
                                                    newPriceKeys: prev.newPriceKeys.filter((_, i) => i !== idx)
                                                }))}
                                            />
                                        ))}
                                        <TextField
                                            variant="standard"
                                            size="small"
                                            placeholder="Type and press Enter"
                                            value={newPriceInput}
                                            onChange={e => setNewPriceInput(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' && newPriceInput.trim()) {
                                                    e.preventDefault();
                                                    const val = newPriceInput.trim();
                                                    if (!state.newPriceKeys.includes(val)) {
                                                        setState(prev => ({...prev, newPriceKeys: [...prev.newPriceKeys, val]}));
                                                    }
                                                    setNewPriceInput('');
                                                }
                                            }}
                                            InputProps={{disableUnderline: true}}
                                            sx={{flex: 1, minWidth: 120}}
                                        />
                                    </Box>
                                </Box>

                            </Grid>

                            {/* Action Buttons */}
                            <Box width='100%'/>
                            <Grid item xs={0} sm={0} md={1} lg={1}/>
                            <Grid item xs={isEditMode ? 1 : 2} sm={isEditMode ? 1 : 2} md={isEditMode ? 2 : 4} lg={isEditMode ? 2 : 6} key="saveBtn">
                                <Button variant="contained" fullWidth={true} style={{height: '54px'}}
                                        onClick={onSaveClicked}>
                                    {isEditMode ? "Update template" : "Create template"}
                                </Button>
                            </Grid>
                            {isEditMode && (
                                <Grid item xs={1} sm={1} md={2} lg={2} key="applyBtn">
                                    <Button variant="contained" color="secondary" fullWidth={true}
                                            style={{height: '54px'}}
                                            onClick={onApplyClicked}>
                                        Apply changes
                                    </Button>
                                </Grid>
                            )}
                            {isEditMode && (
                                <Grid item xs={1} sm={1} md={2} lg={2} key="deleteBtn">
                                    <Button variant="outlined" color="error" fullWidth={true}
                                            style={{height: '54px'}}
                                            onClick={() => setState(prev => ({...prev, deleteDialogOpen: true}))}>
                                        Delete template
                                    </Button>
                                </Grid>
                            )}
                            <Box width='100%'/>
                            <Grid item xs={0} sm={0} md={1} lg={1}/>
                            <Grid item xs={2} sm={2} md={4} lg={6} key="goToList">
                                <Button variant="outlined" fullWidth={true} style={{height: '54px'}}
                                        onClick={() => navigate('/funnels/html-templates')}>
                                    Show all HTML templates
                                </Button>
                            </Grid>
                        </React.Fragment>
                    )}
                </Grid>
            </Stack>

            {/* Error Dialog */}
            <Dialog open={state.dialogOpen}>
                <DialogTitle>Error</DialogTitle>
                <DialogContent>
                    <DialogContentText>{state.dialogMsg}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>Got it</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar Toast */}
            <Snackbar
                open={state.snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setState(prev => ({...prev, snackbarOpen: false}))}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
                <Alert
                    onClose={() => setState(prev => ({...prev, snackbarOpen: false}))}
                    severity={state.snackbarSeverity}
                    variant="filled">
                    {state.snackbarMsg}
                </Alert>
            </Snackbar>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={state.deleteDialogOpen}
                onClose={() => setState(prev => ({...prev, deleteDialogOpen: false}))}>
                <DialogTitle>Delete HTML Template</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this template? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setState(prev => ({...prev, deleteDialogOpen: false}))}>Cancel</Button>
                    <Button onClick={onDeleteClicked} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>


        </div>
    );
}
